import { useCallback } from 'react';
import { gql, useMutation } from '@apollo/client';
import { TFunction, Namespace } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { accountSelectedSelector } from 'atoms';
import { useMessages } from 'hooks';
import { SolicitationValue } from './SolicitationSelect';

const NEW_SERVICE_ORDER = gql`
  mutation (
    $defectId: Int!
    $requesterId: Int
    $description: String
    $accountId: Int!
    $masterCompanyId: Int!
    $webService: String
    $isCloud: Boolean!
  ) {
    NewServiceOrder(
      defectId: $defectId
      requesterId: $requesterId
      description: $description
      accountId: $accountId
      masterCompanyId: $masterCompanyId
      webService: $webService
      isCloud: $isCloud
    ) {
      error
      value
      id
      sequentialId
    }
  }
`;

function useNewServiceOrder({
  t,
  setOpen,
  refetch,
}: {
  t: TFunction<Namespace<'service_order' | '_common'>>;
  setOpen: (opened: boolean) => void;
  refetch: () => void;
}) {
  const accountSelected = useRecoilValue(accountSelectedSelector);
  const { enqueueError, enqueueSuccess } = useMessages();

  const [newServiceOrderMutation, { loading }] = useMutation(NEW_SERVICE_ORDER, {
    onCompleted: (data) => {
      if (!data?.NewServiceOrder?.id) {
        return enqueueError(t('service_order:error_adding_order'));
      }
      enqueueSuccess(data?.NewServiceOrder?.value || t('service_order:order_saved'));
      setOpen(false);
      refetch();
    },
    onError: () => {
      enqueueError(t('service_order:error_adding_order'));
    },
  });

  const getRequestText = useCallback(
    (description: string) => {
      const { email, userName } = accountSelected || {};
      if (!userName && !email) {
        return description;
      }

      const descriptionText = description ? `${description} - ` : '';

      if (!userName) {
        return `${descriptionText}${t('service_order:service_order_requested_by')} ${email}`;
      }

      if (!email) {
        return `${descriptionText}${t('service_order:service_order_requested_by')} ${userName}`;
      }

      return `${descriptionText}${t(
        'service_order:service_order_requested_by',
      )} ${userName} (${email})`;
    },
    [t, accountSelected],
  );

  const newServiceOrder = useCallback(
    ({
      solicitation,
      description,
    }: {
      solicitation: SolicitationValue | null;
      description: string;
    }) => {
      newServiceOrderMutation({
        variables: {
          ...accountSelected,
          accountId: accountSelected?.id || 0,
          description: getRequestText(description),
          defectId: solicitation?.id || 0,
        },
      });
    },
    [newServiceOrderMutation, accountSelected, getRequestText],
  );

  return {
    newServiceOrder,
    loading,
  };
}

export default useNewServiceOrder;
